import { TFunction } from 'i18next';
import { compact, isEmpty, some, trim, values } from 'lodash';

import { getHakuDemo } from '#/src/api/konfoApi';
import {
  Alkamiskausityyppi,
  KOULUTUS_TYYPPI,
  MAKSULLISUUSTYYPPI,
  Koulutustyyppi,
} from '#/src/constants';
import { localize } from '#/src/tools/localization';
import {
  Alkamiskausi,
  KoulutusLisatieto,
  KoutaKoulutustyyppi,
  Translateable,
} from '#/src/types/common';
import { Hakukohde } from '#/src/types/HakukohdeTypes';
import { Maksu } from '#/src/types/ToteutusTypes';

export const formatAloitus = (
  {
    alkamiskausityyppi,
    henkilokohtaisenSuunnitelmanLisatiedot,
    koulutuksenAlkamiskausi,
    koulutuksenAlkamisvuosi,
    formatoituKoulutuksenalkamispaivamaara,
    formatoituKoulutuksenpaattymispaivamaara,
  }: Alkamiskausi = {} as Alkamiskausi,
  t: TFunction
) => {
  switch (alkamiskausityyppi) {
    case Alkamiskausityyppi.HENKILOKOHTAINEN_SUUNNITELMA:
      return {
        alkaaText: t('toteutus.koulutus-alkaa-henkilokohtainen'),
        alkaaModalText: henkilokohtaisenSuunnitelmanLisatiedot,
      };
    case Alkamiskausityyppi.TARKKA_ALKAMISAJANKOHTA:
      return {
        alkaaText: localize(formatoituKoulutuksenalkamispaivamaara),
        paattyyText: localize(formatoituKoulutuksenpaattymispaivamaara),
      };
    case Alkamiskausityyppi.ALKAMISKAUSI_JA_VUOSI:
      return {
        alkaaText: [localize(koulutuksenAlkamiskausi), koulutuksenAlkamisvuosi].join(' '),
      };
    default:
      return {};
  }
};

// KoodiUrien versiot jätetään huomiotta
const otsikkoKoodi = (lisatieto: KoulutusLisatieto) =>
  lisatieto?.otsikko?.koodiUri?.split('#')[0];

const hasTeksti = (lisatieto: KoulutusLisatieto) =>
  some(values(lisatieto?.teksti), (teksti) => !isEmpty(trim(teksti)));

// Toteutukselle tallennettu lisätieto korvaa koulutuksen samalla otsikolla tallennetun
// lisätiedon, jotta samaa lisätietoa ei näytetä toteutussivulla kahteen kertaan.
// Tyhjä toteutuksen lisätieto ei korvaa koulutuksen lisätietoa.
export const combineLisatiedot = (
  koulutuksenLisatiedot?: Array<KoulutusLisatieto> | null,
  toteutuksenLisatiedot?: Array<KoulutusLisatieto> | null
) => {
  const koulutuksenTiedot = koulutuksenLisatiedot ?? [];
  const toteutuksenTiedot = toteutuksenLisatiedot ?? [];

  const korvaavatByOtsikko = new Map(
    toteutuksenTiedot
      .filter((lisatieto) => otsikkoKoodi(lisatieto) && hasTeksti(lisatieto))
      .map((lisatieto) => [otsikkoKoodi(lisatieto), lisatieto])
  );
  const koulutuksenOtsikot = new Set(compact(koulutuksenTiedot.map(otsikkoKoodi)));

  return [
    // Korvattu lisätieto säilyttää koulutuksen lisätietojen mukaisen järjestyksen
    ...koulutuksenTiedot.map(
      (lisatieto) => korvaavatByOtsikko.get(otsikkoKoodi(lisatieto)) ?? lisatieto
    ),
    ...toteutuksenTiedot.filter((lisatieto) => {
      const koodi = otsikkoKoodi(lisatieto);
      return !koodi || !koulutuksenOtsikot.has(koodi);
    }),
  ];
};

const formDemoLink = (link: Translateable): Translateable => {
  const transform = (langLink: string | undefined) => {
    if (langLink && !isEmpty(langLink)) {
      return langLink.includes('?')
        ? langLink.concat('&demo=true')
        : langLink.concat('?demo=true');
    }
    return langLink;
  };
  return {
    fi: transform(link.fi),
    en: transform(link.en),
    sv: transform(link.sv),
  };
};

export const demoLinksPerLomakeId = async (
  hakukohteet: Array<Hakukohde>
): Promise<Map<string, undefined | Map<string, Translateable>>> => {
  const closedHakukohteet = hakukohteet.filter((hakukohde) => !hakukohde.isHakuAuki);
  const result: Map<string, undefined | Map<string, Translateable>> = new Map();

  for (const hakukohde of closedHakukohteet) {
    const lomakeId = hakukohde.hakulomakeAtaruId;
    const hakukohdeOid = hakukohde.hakukohdeOid;
    const hakuOid = hakukohde.hakuOid;
    if (!result.has(lomakeId)) {
      const hakukohdeDemo = await getHakuDemo(hakuOid);
      if (hakukohdeDemo.demoAllowed) {
        const linkMap: Map<string, Translateable> = new Map();
        linkMap.set(hakukohdeOid, formDemoLink(hakukohde.hakulomakeLinkki));
        result.set(lomakeId, linkMap);
      } else {
        result.set(lomakeId, undefined);
      }
    } else if (result.get(lomakeId)) {
      result.get(lomakeId)?.set(hakukohdeOid, formDemoLink(hakukohde.hakulomakeLinkki));
    }
  }

  return result;
};

export const isLukuvuosimaksullinen = (maksut?: Array<Maksu>) => {
  return some(
    maksut,
    (mt) => mt?.maksullisuustyyppi === MAKSULLISUUSTYYPPI.LUKUVUOSIMAKSU
  );
};

export const isLukuvuosimaksullinenKkToteutus = (
  maksut?: Array<Maksu>,
  koulutustyyppi?: KoutaKoulutustyyppi
) => {
  return (
    maksut?.length === 1 &&
    isLukuvuosimaksullinen(maksut) &&
    [KOULUTUS_TYYPPI.AMKKOULUTUS, KOULUTUS_TYYPPI.YLIOPISTOKOULUTUS].includes(
      koulutustyyppi as Koulutustyyppi
    )
  );
};

export const formatMaksullisuusTitle = (
  t: TFunction,
  maksut?: Array<Maksu>,
  koulutustyyppi?: KoutaKoulutustyyppi
): string => {
  if (isLukuvuosimaksullinenKkToteutus(maksut, koulutustyyppi)) {
    return t('toteutus.lukuvuosimaksu-kk');
  }

  if (isLukuvuosimaksullinen(maksut) && maksut?.length === 1) {
    return t('toteutus.lukuvuosimaksu');
  }

  return t('toteutus.maksullisuus');
};

const getMaksunMaara = (maksu?: Array<Maksu>): string => {
  const maksunMaara = maksu?.[0]?.maksunMaara;
  return maksunMaara ? `${maksunMaara} €` : '';
};

const formatMaksullisuusRow = (otsikko: string, maksunmaara: string): string => {
  if (maksunmaara) {
    return `${otsikko}: ${maksunmaara}`;
  } else {
    return otsikko;
  }
};

export const formatMaksullisuusText = (t: TFunction, maksut?: Array<Maksu>) => {
  const maksullinen = maksut?.filter(
    (maksu) => maksu.maksullisuustyyppi === MAKSULLISUUSTYYPPI.MAKSULLINEN
  );
  const lukuvuosimaksu = maksut?.filter(
    (maksu) => maksu.maksullisuustyyppi === MAKSULLISUUSTYYPPI.LUKUVUOSIMAKSU
  );

  const maksunMaara = getMaksunMaara(maksullinen);
  const lukuvuosimaksunMaara = getMaksunMaara(lukuvuosimaksu);

  if (!isEmpty(maksullinen) && !isEmpty(lukuvuosimaksu)) {
    const maksullinenStr = formatMaksullisuusRow(
      t('toteutus.maksullinen-opetus'),
      maksunMaara
    );
    const lukuvuosimaksuStr = formatMaksullisuusRow(
      t('toteutus.lukuvuosimaksu'),
      lukuvuosimaksunMaara
    );
    return `${maksullinenStr}\n${lukuvuosimaksuStr}`;
  }

  if (!isEmpty(maksullinen)) {
    return maksunMaara;
  }

  if (!isEmpty(lukuvuosimaksu)) {
    return lukuvuosimaksunMaara;
  }

  return t('toteutus.ei-maksua');
};
